import { LLMModel, NLPBrainActionType } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonVariant, Input, LineHeader, Select } from "@swivl-mono/ui";
import { useState } from "react";
import { NavigateFunction } from "react-router-dom";

import { BrainsModel } from "../../../../Models/Brains/Brains.Model";
import ModalModel from "../../../../Models/Modal/Modal.Model";
import NLPModel from "../../../../Models/NLP/NLP.Model";
import "./AddBrainsModal.scss";

interface Props {
  navigate: NavigateFunction;
}

export function AddBrainsModal(props: Props) {
  const { navigate } = props;
  const [loading, setLoading] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("NLP");
  const [brainName, setBrainName] = useState("New Brain");
  const [model, setModel] = useState<LLMModel["model"] | null>(null);
  const [thresholds, setThresholds] = useState({ intent: 24, entity: 35 });

  const { cachesMap } = NLPModel.useState((state) => state);

  const createNLPBrain = async () => {
    if (!model) {
      alert("Please select a model");
      return;
    }
    setLoading(true);

    const brain = await BrainsModel.createNLPBrain(
      brainName,
      {
        actions: {},
        unhandled: {
          type: NLPBrainActionType.QUEST
        },
        thresholds: {
          entity: thresholds.entity * 0.01,
          intent: thresholds.intent * 0.01
        }
      },
      {
        appId: cachesMap![model].appId,
        version: cachesMap![model].version
      }
    );

    setLoading(false);
    navigate(`/brain/${brain.slug}`);
    ModalModel.hideModal();
  };

  const createLLMBrain = async () => {
    if (!model) {
      alert("Please select a model");
      return;
    }
    setLoading(true);

    const brain = await BrainsModel.createLLMBrain(
      brainName,
      {
        prompt: "Please respond to the above messages.",
        numberOfMessagesToInclude: 15,
        numberOfTokensInResponse: 200
      },
      {
        model: model
      }
    );

    setLoading(false);
    navigate(`/brain/${brain.slug}`);
    ModalModel.hideModal();
  };

  return (
    <div className="AddBrainsModal">
      <LineHeader
        menuItems={["NLP", "LLM"]}
        selectedMenuItem={selectedMenuItem}
        menuItemSelected={(selectedMenuItem) => {
          setModel(null);
          setSelectedMenuItem(selectedMenuItem);
        }}
      />
      {selectedMenuItem === "NLP" ? (
        <div className="flex flex-col gap-2">
          <Input label="Brain Name" value={brainName} onChange={(e) => setBrainName(e.target.value)} />

          <Select
            label="Model"
            options={cachesMap && Object.keys(cachesMap).map((cacheId) => ({ label: cachesMap[cacheId].name, value: cacheId }))}
            selectedValues={model ? [model] : []}
            onChange={(vals) => setModel((vals[0] as LLMModel["model"]) || null)}
          />

          <Input
            type="number"
            label="Intent Threshold"
            suffix="%"
            value={String(thresholds.intent)}
            onChange={(e) => {
              setThresholds({ ...thresholds, intent: Number(e.target.value) });
            }}
          />

          <Input
            type="number"
            label="Entity Threshold"
            suffix="%"
            value={String(thresholds.entity)}
            onChange={(e) => {
              setThresholds({ ...thresholds, entity: Number(e.target.value) });
            }}
          />

          <div className="mt-4 flex justify-end gap-2">
            <Button variant={ButtonVariant.GHOST} onClick={() => ModalModel.hideModal()}>
              Cancel
            </Button>
            <Button
              color={ButtonColor.YELLOW}
              loading={loading}
              onClick={() => {
                createNLPBrain();
              }}
            >
              Create
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Input label="Brain Name" value={brainName} onChange={(e) => setBrainName(e.target.value)} />
          <Select
            label="Model"
            options={[
              { label: "GPT 3.5 Turbo", value: "gpt-3.5-turbo" },
              { label: "GPT 4", value: "gpt-4" },
              { label: "GPT 4.1", value: "gpt-4.1" }
            ]}
            selectedValues={model ? [model] : []}
            onChange={(vals) => setModel((vals[0] as LLMModel["model"]) || null)}
          />

          <div className="mt-2 flex justify-end gap-2">
            <Button variant={ButtonVariant.GHOST} onClick={() => ModalModel.hideModal()}>
              Cancel
            </Button>
            <Button color={ButtonColor.YELLOW} loading={loading} onClick={() => createLLMBrain()}>
              Create
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
